import type { EstadoConexao } from './realtime';
import type { LicenseInfo } from './license';

/**
 * Chaves do namespace `app` (e parâmetros), não texto pronto: este módulo não tem idioma, e a
 * faixa precisa acompanhar a troca de idioma sem recalcular o aviso. `contato` é a orientação
 * que segue a frase principal (`avisos.contato.<contato>`).
 */
export type Aviso = {
  nivel: 'erro' | 'alerta';
  chave: string;
  params?: Record<string, string | number>;
  contato?: 'vyxora' | 'administrador';
};

/** Dias antes do vencimento em que a faixa aparece. */
export const DIAS_AVISO_LICENCA = 30;

const MS_DIA = 86_400_000;

const CHAVE_STATUS: Record<Exclude<LicenseInfo['status'], 'Ativa'>, string> = {
  Expirada: 'avisos.licenca.expirada',
  Suspensa: 'avisos.licenca.suspensa',
  Cancelada: 'avisos.licenca.cancelada',
};

/**
 * Aviso de licença para o cliente ativo, ou null. Regras:
 * - status diferente de Ativa → erro (a operação já está bloqueada ou vai estar);
 * - Ativa, mas `validaAte` no passado → erro (o backend ainda não troca o status sozinho);
 * - vence em até 30 dias → alerta.
 *
 * `semLicenca` é o 404 NO_LICENSE do backend.
 */
export function avisoLicenca(
  info: LicenseInfo | undefined,
  semLicenca: boolean,
  isAdmin: boolean,
  agora = Date.now(),
): Aviso | null {
  const contato = isAdmin ? 'vyxora' : 'administrador';

  if (semLicenca) {
    return { nivel: 'erro', chave: 'avisos.licenca.semLicenca', contato };
  }
  if (!info) return null;

  if (info.status !== 'Ativa') {
    return { nivel: 'erro', chave: CHAVE_STATUS[info.status], contato };
  }

  const dias = Math.ceil((new Date(info.validaAte).getTime() - agora) / MS_DIA);
  if (dias < 0) {
    return { nivel: 'erro', chave: 'avisos.licenca.venceu', contato };
  }
  if (dias <= DIAS_AVISO_LICENCA) {
    if (dias === 0) return { nivel: 'alerta', chave: 'avisos.licenca.venceHoje', contato };
    if (dias === 1) return { nivel: 'alerta', chave: 'avisos.licenca.venceAmanha', contato };
    return { nivel: 'alerta', chave: 'avisos.licenca.venceEmDias', params: { count: dias }, contato };
  }
  return null;
}

/**
 * Aviso de conectividade, ou null. Sem internet vence sobre o estado do hub: é a causa, e a
 * mensagem certa. `inativa` e `conectando` não avisam — é o começo normal da sessão.
 */
export function avisoConexao(estado: EstadoConexao, online: boolean): Aviso | null {
  if (!online) {
    return { nivel: 'erro', chave: 'avisos.conexao.offline' };
  }
  if (estado === 'reconectando') {
    return { nivel: 'alerta', chave: 'avisos.conexao.reconectando' };
  }
  if (estado === 'caiu') {
    return { nivel: 'alerta', chave: 'avisos.conexao.caiu' };
  }
  return null;
}
